import { openDB, DBSchema, IDBPDatabase } from "idb";

interface OfflinePhoto {
  id: string;
  orderId: string;
  folder: string; // "checkin" | "water-test" | "final"
  blob: Blob;
  timestamp: number;
}

interface PendingSync {
  id: string;
  orderId: string;
  type: "checkin" | "save_progress" | "finalize";
  payload: any;
  photos: OfflinePhoto[];
  createdAt: number;
  status: "pending" | "syncing" | "failed";
  retries: number;
}

interface OfflineDB extends DBSchema {
  pendingSync: {
    key: string;
    value: PendingSync;
    indexes: { "by-status": string };
  };
  offlinePhotos: {
    key: string;
    value: OfflinePhoto;
    indexes: { "by-orderId": string };
  };
  cachedOrders: {
    key: string;
    value: {
      id: string;
      data: any;
      cachedAt: number;
    };
  };
  cachedPools: {
    key: string;
    value: {
      id: string;
      data: any;
      cachedAt: number;
    };
  };
}

let dbPromise: Promise<IDBPDatabase<OfflineDB>> | null = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<OfflineDB>("aquapool-offline", 1, {
      upgrade(db) {
        const syncStore = db.createObjectStore("pendingSync", { keyPath: "id" });
        syncStore.createIndex("by-status", "status");

        const photoStore = db.createObjectStore("offlinePhotos", { keyPath: "id" });
        photoStore.createIndex("by-orderId", "orderId");

        db.createObjectStore("cachedOrders", { keyPath: "id" });
        db.createObjectStore("cachedPools", { keyPath: "id" });
      },
    });
  }
  return dbPromise;
}

// Pending sync operations
export async function addPendingSync(item: Omit<PendingSync, "id" | "createdAt" | "status" | "retries">) {
  const db = await getDb();
  const entry: PendingSync = {
    ...item,
    id: `sync-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: Date.now(),
    status: "pending",
    retries: 0,
  };
  await db.put("pendingSync", entry);
  return entry;
}

export async function getPendingSyncs() {
  const db = await getDb();
  return db.getAllFromIndex("pendingSync", "by-status", "pending");
}

export async function getAllPendingSyncs() {
  const db = await getDb();
  return db.getAll("pendingSync");
}

export async function updateSyncStatus(id: string, status: PendingSync["status"], retries?: number) {
  const db = await getDb();
  const item = await db.get("pendingSync", id);
  if (item) {
    item.status = status;
    if (retries !== undefined) item.retries = retries;
    await db.put("pendingSync", item);
  }
}

export async function deletePendingSync(id: string) {
  const db = await getDb();
  await db.delete("pendingSync", id);
}

// Offline photos
export async function saveOfflinePhoto(photo: OfflinePhoto) {
  const db = await getDb();
  await db.put("offlinePhotos", photo);
}

export async function getOfflinePhotosByOrder(orderId: string) {
  const db = await getDb();
  return db.getAllFromIndex("offlinePhotos", "by-orderId", orderId);
}

export async function deleteOfflinePhoto(id: string) {
  const db = await getDb();
  await db.delete("offlinePhotos", id);
}

// Cached data for offline access
export async function cacheOrder(id: string, data: any) {
  const db = await getDb();
  await db.put("cachedOrders", { id, data, cachedAt: Date.now() });
}

export async function getCachedOrder(id: string) {
  const db = await getDb();
  return db.get("cachedOrders", id);
}

export async function cachePool(id: string, data: any) {
  const db = await getDb();
  await db.put("cachedPools", { id, data, cachedAt: Date.now() });
}

export async function getCachedPool(id: string) {
  const db = await getDb();
  return db.get("cachedPools", id);
}

export async function getPendingSyncCount() {
  const db = await getDb();
  const items = await db.getAllFromIndex("pendingSync", "by-status", "pending");
  return items.length;
}
